import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiSearch, FiBookmark, FiBell, FiImage, FiLink, FiCode, FiType, FiEdit, FiEye, FiHeart, FiMessageSquare, FiClock, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { BiBold, BiItalic, BiUnderline, BiHeading } from 'react-icons/bi';
import { BsBlockquoteLeft, BsCode, BsThreeDots } from 'react-icons/bs';
import { MdFormatListBulleted, MdFormatListNumbered } from 'react-icons/md';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import Header from './components/Header';
import ArticleDraftPage from './pages/ArticleDraftPage';
import ArticlePage from './pages/ArticlePage';
// import { ArticleProvider, useArticles } from './context/ArticleContext';
import ContactPage from './pages/ContactPage';
import AdminPage from './pages/AdminPage';
import SearchPage from './pages/SearchPage';
import BookmarkPage from './pages/BookmarkPage';
import AboutPage from './pages/AboutPage';
import config from './amplify_outputs.json';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/data';
import { getUrl } from 'aws-amplify/storage';
import ShortUrlRedirect from './components/ShortUrlRedirect';
import NotFoundPage from './pages/NotFoundPage';
import { HelmetProvider } from 'react-helmet-async';
import MetaTags from './components/MetaTags';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Move Tooltip to the top, before other styled components
const Tooltip = styled.span`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 1rem;
  background: #292929;
  color: white;
  padding: 0.5rem 0.8rem;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 1000;

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #292929;
  }
`;

const sanitizeContent = (content) => {
  if (!content) return '';
  // Remove HTML tags using regex instead of DOM manipulation
  return content
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&[^;]+;/g, '') // Remove HTML entities
    .trim();
};

// Configure Amplify
Amplify.configure(config);

// Initialize the data client
const client = generateClient();

// Update getS3ImageUrl function
const getS3ImageUrl = async (key) => {
  if (!key) return null;
  try {
    const { url } = await getUrl({ path: key });
    return url;
  } catch (err) {
    return null;
  }
};

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const carouselRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeout = useRef(null);

  // Auto-scroll effect for carousel with pause functionality
  useEffect(() => {
    if (isPaused) return; // Don't set interval if paused

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused]); // Re-run effect when isPaused changes

  const pauseAutoScroll = () => {
    setIsPaused(true);
    
    // Clear any existing timeout
    if (pauseTimeout.current) {
      clearTimeout(pauseTimeout.current);
    }

    // Resume auto-scroll after 3 seconds
    pauseTimeout.current = setTimeout(() => {
      setIsPaused(false);
    }, 3000);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    pauseAutoScroll(); // Pause when user starts touching
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) < minSwipeDistance) return;

    if (distance > 0) {
      // Swiped left, go to next slide
      setCurrentSlide((prev) => (prev + 1) % carouselArticles.length);
    } else {
      // Swiped right, go to previous slide
      setCurrentSlide((prev) => (prev - 1 + carouselArticles.length) % carouselArticles.length);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Also pause on dot click
  const handleDotClick = (index) => {
    setCurrentSlide(index);
    pauseAutoScroll();
  };

  useEffect(() => {
    // Load bookmarked IDs from localStorage
    const ids = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
    setBookmarkedIds(ids);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchArticles = async () => {
      try {
        const response = await client.models.Article.list({
          limit: 10,
          sort: { field: 'createdAt', direction: 'desc' }
        });
        
        if (!isMounted) return;

        // Process the articles and get signed URLs for images
        const processedArticles = await Promise.all(response.data.map(async article => {
          const fullImageUrl = await getS3ImageUrl(article.bannerImageUrl);
          const sanitizedContent = sanitizeContent(article.content);
          return {
            ...article,
            bannerImageUrl: fullImageUrl,
            content: sanitizedContent,
            description: sanitizedContent.slice(0, 200) + (sanitizedContent.length > 200 ? '...' : '')
          };
        }));
        
        setArticles(processedArticles);
        setError(null);
      } catch (err) {
        console.error('Error fetching articles:', err);
        if (isMounted) {
          setError('Failed to load articles');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchArticles();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if email already exists
      const existingSubscribers = await client.models.NewsletterSubscriber.list({
        filter: {
          email: { eq: email }
        }
      });

      if (existingSubscribers.data.length > 0) {
        alert('You are already subscribed to our newsletter!');
        return;
      }

      // Create new newsletter subscriber
      await client.models.NewsletterSubscriber.create({
        email,
        status: 'active'
      });

      alert('Thank you for subscribing to our newsletter!');
      setEmail('');
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      alert('Failed to subscribe. Please try again later.');
    }
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselArticles.length) % carouselArticles.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselArticles.length);
  };

  const handleStoriesScroll = (direction) => {
    const container = document.querySelector('.stories-track-wrapper');
    const scrollAmount = 300;
    
    if (direction === 'next') {
      // If we're at the end, scroll back to start
      if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    } else {
      // If we're at the start, scroll to end
      if (container.scrollLeft === 0) {
        container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    }
  };

  if (loading) return <LoadingMessage>Loading articles...</LoadingMessage>;
  if (error) return <ErrorMessage>{error}</ErrorMessage>;

  // Sort all articles by views
  const sortedArticles = [...articles].sort((a, b) => (b.views || 0) - (a.views || 0));

  // Get top 3 articles for carousel
  const carouselArticles = sortedArticles.slice(0, 3);
  
  // Get remaining articles for the scrollable section
  const remainingArticles = sortedArticles.slice(3);

  return (
    <>
      <MetaTags
        title="Parenting Beyond Perfection"
        description="Discover authentic parenting stories, tips, and insights for your journey beyond perfection."
        type="website"
      />
      <HomeContainer>
        <CarouselSection>
          <CarouselWrapper
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <CarouselTrack currentSlide={currentSlide}>
              {carouselArticles.map((article, index) => (
                <CarouselSlide 
                  key={article.id} 
                  to={`/article/${article.id}`}
                >
                  <CarouselImage 
                    src={article.bannerImageUrl} 
                    alt={article.title}
                  />
                  <CarouselContent>
                    <CarouselMainContent>
                      <CarouselTitle>{article.title}</CarouselTitle>
                      <CarouselDescription>
                        {article.content}
                      </CarouselDescription>
                    </CarouselMainContent>
                    <CarouselMetadata>
                      <CarouselAuthorRow>
                        <AuthorImage />
                        <AuthorDetails>
                          <AuthorName>{article.author}</AuthorName>
                          <span>{new Date(article.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric'
                          })}</span>
                        </AuthorDetails>
                      </CarouselAuthorRow>
                      <CarouselStats>
                        <StatItem><FiEye /> {article.views || 0}</StatItem>
                        <StatItem><FiHeart /> {article.likes || 0}</StatItem>
                        <StatItem><FiClock /> {article.readTime || 5} min</StatItem>
                        <StatItem isBookmarked={bookmarkedIds.includes(article.id)}>
                          <FiBookmark />
                        </StatItem>
                      </CarouselStats>
                    </CarouselMetadata>
                  </CarouselContent>
                </CarouselSlide>
              ))}
            </CarouselTrack>
          </CarouselWrapper>
          <CarouselDots>
            {carouselArticles.map((_, index) => (
              <CarouselDot
                key={index}
                active={currentSlide === index}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </CarouselDots>
        </CarouselSection>

        <NewsletterSection>
          <NewsletterContent>
            <NewsletterTitle>Stay Updated</NewsletterTitle>
            <NewsletterDescription>
              Get the latest parenting tips and stories delivered directly to your inbox.
            </NewsletterDescription>
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <NewsletterInput
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <NewsletterButton type="submit">Subscribe</NewsletterButton>
            </NewsletterForm>
          </NewsletterContent>
        </NewsletterSection>

        <StoriesSection>
          <SectionTitle>More Stories</SectionTitle>
          <StoriesContainer>
            <StoriesTrackWrapper className="stories-track-wrapper">
              <StoriesTrack>
                {remainingArticles.map(article => (
                  <ArticleCard key={article.id} to={`/article/${article.id}`}>
                    <ImageWrapper>
                      {article.bannerImageUrl && (
                        <ArticleCardImage 
                          src={article.bannerImageUrl} 
                          alt={article.title}
                        />
                      )}
                    </ImageWrapper>
                    <ArticleCardContent>
                      <ContentWrapper>
                        <ArticleTitle>{article.title}</ArticleTitle>
                        <ArticleDescription>
                          {article.description}
                        </ArticleDescription>
                      </ContentWrapper>
                      <MetadataContainer>
                        <AuthorRow>
                          <AuthorImage />
                          <AuthorDetails>
                            <AuthorName>{article.author}</AuthorName>
                            <span>{new Date(article.createdAt).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric'
                            })}</span>
                          </AuthorDetails>
                        </AuthorRow>
                        <ArticleStats>
                          <StatItem><FiEye /> {article.views || 0}</StatItem>
                          <StatItem><FiHeart /> {article.likes || 0}</StatItem>
                          <StatItem><FiClock /> {article.readTime || 5} min</StatItem>
                          <StatItem isBookmarked={bookmarkedIds.includes(article.id)}>
                            <FiBookmark />
                          </StatItem>
                        </ArticleStats>
                      </MetadataContainer>
                    </ArticleCardContent>
                  </ArticleCard>
                ))}
              </StoriesTrack>
            </StoriesTrackWrapper>
          </StoriesContainer>
          <StoriesNavigation>
            <CarouselButton 
              direction="prev" 
              onClick={() => handleStoriesScroll('prev')}
              aria-label="Previous stories"
            >
              <FiChevronLeft size={24} />
            </CarouselButton>
            <CarouselButton 
              direction="next" 
              onClick={() => handleStoriesScroll('next')}
              aria-label="Next stories"
            >
              <FiChevronRight size={24} />
            </CarouselButton>
          </StoriesNavigation>
        </StoriesSection>
      </HomeContainer>
    </>
  );
};

// Separate component to handle path updates
const AppContent = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const location = useLocation();

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  return (
    <Layout>
      <Header 
        currentPath={currentPath}
        isDraftPage={currentPath === '/new-story'}
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/new-story" element={<ArticleDraftPage />} />
        <Route path="/article/:id" element={<ArticlePage />} />
        <Route path="/article/:id/edit" element={<ArticleDraftPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/bookmarks" element={<BookmarkPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/s/:shortCode" element={<ShortUrlRedirect />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
};

const Layout = styled.div`
  --header-height: 65px;
  --header-height-mobile: 110px;
  min-height: 100vh;
  padding-top: var(--header-height);
  background: #f8f9fa;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    padding-top: var(--header-height-mobile);
  }
`;

// Add PageContainer for consistent page layouts
export const PageContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 2rem;
  background: white;
  color: #757575;
  font-size: 0.9rem;
  border-top: 1px solid #f0f0f0;
  margin-top: 4rem;
`;

const FooterContent = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.5rem;

  a {
    color: #1a8917;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <Layout>
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/new-story" element={<ArticleDraftPage />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/article/:id/edit" element={<ArticleDraftPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/bookmarks" element={<BookmarkPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/s/:shortCode" element={<ShortUrlRedirect />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer>
            <FooterContent>
              <FooterLinks>
                <Link to="/about">About Us</Link>
                <Link to="/contact">Contact</Link>
                <Link to="/privacy">Privacy Policy</Link>
              </FooterLinks>
              <div>© {new Date().getFullYear()} Your Parenting Tips. All rights reserved.</div>
            </FooterContent>
          </Footer>
        </Layout>
      </Router>
    </HelmetProvider>
  );
};

// Remove the Container component since we're using Layout
// Update HomeContainer to remove redundant padding
const HomeContainer = styled.div`
  width: 100%;
  padding: 0 2rem;

  @media (max-width: 1024px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

// Styled Components
const CarouselSection = styled.section`
  position: relative;
  max-width: 1400px;
  width: 90%;
  margin: 2rem auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  height: 85vh;
  min-height: 650px;
  max-height: 850px;
  padding: 10px;

  @media (max-width: 1024px) {
    width: 95%;
    min-height: 550px;
  }

  @media (max-width: 768px) {
    margin: 1rem auto;
    width: 100%;
    height: 80vh;
    min-height: 500px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: white;

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  width: 300%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentSlide * 33.333}%);
`;

const CarouselSlide = styled(Link)`
  width: 33.333%;
  height: 100%;
  text-decoration: none;
  color: inherit;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  display: flex;
  flex-direction: column-reverse;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  min-height: 200px;
`;

const CarouselContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  background: white;
  gap: 1.5rem;
  flex-shrink: 0;
  
  @media (max-width: 1024px) {
    padding: 1.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CarouselMainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CarouselMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  color: #757575;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    gap: 0.75rem;
  }
`;

const CarouselTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Source Serif 4', Georgia, serif;
  color: #292929;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    font-size: 1.75rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    line-height: 1.4;
  }
`;

const CarouselDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.5;
  color: #757575;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.4;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 10px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  z-index: 10;
  transform: translateY(-40%);
  margin-top: 10px;
`;

const CarouselDot = styled.button`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#1a8917' : '#e6e6e6'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    background: ${props => props.active ? '#1a8917' : '#d0d0d0'};
    transform: scale(1.2);
  }
`;

const StoriesSection = styled.section`
  max-width: 1300px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  min-height: 50vh;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    gap: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1.5rem;
  color: #292929;
  padding: 0 0.5rem;
`;

const StoriesContainer = styled.div`
  width: 100%;
  position: relative;
`;

const StoriesTrackWrapper = styled.div`
  flex: 1;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StoriesTrack = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 0.5rem 0.25rem;
  width: fit-content;
`;

const StoriesNavigation = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ArticleCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  width: 300px;
  min-width: 300px;
  height: auto;
  min-height: 420px;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    width: 280px;
    min-width: 280px;
    min-height: 400px;
  }

  @media (max-width: 480px) {
    width: 260px;
    min-width: 260px;
    min-height: 380px;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  background: #f8f9fa;

  @media (max-width: 480px) {
    height: 180px;
  }
`;

const ArticleCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const ArticleCardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1.5rem;
  gap: 1.25rem;

  @media (max-width: 480px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const ArticleTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #292929;
  font-family: 'Source Serif 4', Georgia, serif;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(1.1rem * 1.4 * 2);

  @media (max-width: 480px) {
    font-size: 1rem;
    min-height: calc(1rem * 1.4 * 2);
  }
`;

const ArticleDescription = styled.p`
  color: #757575;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(0.9rem * 1.6 * 3);

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.5;
    min-height: calc(0.85rem * 1.5 * 3);
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-top: 1px solid #f0f0f0;
  padding-top: 1rem;
  margin-top: auto;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const ArticleStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #757575;
  font-size: 0.75rem;
  flex-wrap: wrap;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: ${props => props.isBookmarked ? '#1a8917' : '#757575'};

  svg {
    fill: ${props => props.isBookmarked ? 'currentColor' : 'none'};
  }
`;

const AuthorDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #757575;
  font-size: 0.75rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    &:not(:last-child)::after {
      content: "·";
      margin: 0 0.3rem;
    }
  }
`;

const AuthorName = styled.span`
  font-weight: 500;
  color: #292929;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NewsletterSection = styled.section`
  padding: 2rem;
  background: #f8f9fa;
  text-align: center;
  margin-top: 1rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const NewsletterContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const NewsletterTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 1rem;
  color: #292929;
`;

const NewsletterDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #757575;
  margin: 0 0 2rem;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 500px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 0.8rem 1.2rem;
  border: 1px solid #e6e6e6;
  border-radius: 99px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: #1a8917;
  }
`;

const NewsletterButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 99px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #156912;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #757575;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e53935;
  font-size: 1.2rem;
`;

const AuthorImage = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: url('/author.jpeg');
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const CarouselAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;

  @media (max-width: 768px) {
    gap: 0.35rem;
  }
`;

const CarouselStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #757575;
  font-size: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    gap: 0.75rem;
  }

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const CarouselButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

export default App;